import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { IShoppingListCategory } from '@/interfaces/ShoppingList';
import { IPromotion } from '@/interfaces/Promotion';
import { ICard } from '@/interfaces/Card';
import {
	ICallToAction,
	CallToActionType,
	CallToActionPosition,
} from '@/interfaces/CallToActionTypes';
import { Partner } from '@/interfaces/Partner';
import { PromotionCarouselItem } from '@/interfaces/PromotionCarouselItem';
import {
	UPDATE_PRODUCT_SHOPPING_LIST_ITEM,
	OPEN_MXR_SWAP_MODAL,
	UPDATE_SWAP_OPTIONS,
	GET_ITEM_ID_OFFERS_COUNT,
	GET_MAX_OFFERS,
	GET_CURRENT_CARD,
	GET_SWAP_ENABLED,
	GET_PRODUCT_SHOPPING_LIST_SECTIONS,
	GET_SWAP_OPEN,
	GET_IS_DEFAULT,
	GET_VISIBLE_PROMOTIONS,
	GET_TAB_CTA,
	GET_DESELECT_CLICKED_REPORT,
	SEND_REPORT,
	GET_SELECT_CLICKED_REPORT,
} from '@/store/types';
import { ProductShoppingListItem } from '@/interfaces/ProductShoppingListItem';
import { VuexAction } from '@/@types/index.d';
import { IReport } from '@/interfaces/IReport';
import Swap from '../swap/Swap.vue';

export class ProductShoppingListSection {
	public Heading: string;

	public Items: ProductShoppingListItem[];

	constructor(heading: string, items: ProductShoppingListItem[]) {
		this.Heading = heading;
		this.Items = items;
	}

	public static createFromShoppingListCategory(
		sl: IShoppingListCategory
	): ProductShoppingListSection {
		return new ProductShoppingListSection(
			sl.name,
			sl.items.map(ProductShoppingListItem.createFromShoppingListItem)
		);
	}

	public static createFromCarousel(
		carousel: PromotionCarouselItem
	): ProductShoppingListSection {
		if (carousel.promotions.length === 0) {
			return new ProductShoppingListSection(
				carousel.heading,
				carousel.defaultPromotions.map(
					ProductShoppingListItem.createFromPromotion
				)
			);
			// eslint-disable-next-line no-else-return
		} else {
			return new ProductShoppingListSection(
				carousel.heading,
				carousel.promotions.map(
					ProductShoppingListItem.createFromPromotion
				)
			);
		}
	}
}

@Component({
	components: {
		Swap,
	},
})
export default class ProductShoppingList extends Vue {
	@Getter(GET_CURRENT_CARD) getCurrentCard!: ICard;

	@Getter(GET_TAB_CTA) getTabCTA!: ICallToAction;

	@Getter(GET_VISIBLE_PROMOTIONS) getVisiblePromotions!: IPromotion[];

	@Getter(GET_SWAP_ENABLED) getSwapEnabled!: boolean;

	@Getter(GET_PRODUCT_SHOPPING_LIST_SECTIONS)
	getProductShoppingListSections!: ProductShoppingListSection[];

	@Getter(GET_SWAP_OPEN) getSwapOpen!: boolean;

	@Getter(GET_IS_DEFAULT) getIsDefault!: boolean;

	@Getter(GET_MAX_OFFERS) getMaxOffers!: (header: string) => number;

	@Getter(GET_ITEM_ID_OFFERS_COUNT) getItemIdOfferCount!: Map<string, number>;

	@Getter(GET_DESELECT_CLICKED_REPORT) getDeselectClickedReport!: IReport;

	@Getter(GET_SELECT_CLICKED_REPORT) getSelectClickedReport!: IReport;

	@Action(UPDATE_PRODUCT_SHOPPING_LIST_ITEM)
	updateProductShoppingListItem!: VuexAction<ProductShoppingListItem>;

	@Action(OPEN_MXR_SWAP_MODAL) openMxrSwapModal!: VuexAction<{
		item: ProductShoppingListItem;
		swapSection: number;
	}>;

	@Action(UPDATE_SWAP_OPTIONS)
	updateSwapOptions!: VuexAction<ProductShoppingListItem>;

	@Action(SEND_REPORT) sendReport!: VuexAction<IReport>;

	private previouslyOpened = false;

	/*
    get sections(): ProductShoppingListSection[] {
      const items: ProductShoppingListSection[] = [];
      this.shoppingList.map(ProductShoppingListSection.createFromShoppingListCategory)
        .forEach(i => items.push(i));

      this.carousels
        .map(ProductShoppingListSection.createFromCarousel)
        .forEach(i => items.push(i));

      return items;
    }
    */

	get visiblePromotionIds(): string[] {
		return this.getVisiblePromotions.map((p) => p.id.toString());
	}

	get position(): CallToActionPosition {
		return CallToActionPosition.TAB;
	}

	get ctaType(): CallToActionType {
		return this.getTabCTA.ctaType;
	}

	get showCTA(): boolean {
		return this.getTabCTA.ctaType !== 'none';
	}

	get url(): string {
		return this.ctaType === CallToActionType.PARTNER
			? this.getCurrentCard.callToActionUrl || ''
			: this.getTabCTA.url;
	}

	get subject(): string {
		return this.getTabCTA.emailSubject;
	}

	get body(): string {
		return this.getTabCTA.emailBody;
	}

	get partner(): Partner {
		return this.getTabCTA.partner;
	}

	get ctaCheckoutTotal(): boolean {
		return this.getTabCTA.showCheckoutTotal;
	}

	get label() {
		return this.getTabCTA.label;
	}

	get isSwapComponentOpen() {
		if (!this.getSwapOpen) {
			return 'is-visible';
		}
		return 'is-hidden';
	}

	private hasOffers(itemId: string): boolean {
		const num = this.getItemIdOfferCount.get(itemId);
		return num !== undefined && num > 1;
	}

	private hasOfferToDisplay(i: ProductShoppingListItem) {
		return i.Description && i.Description.length;
	}

	private selectedChanged(li: ProductShoppingListItem) {
		// eslint-disable-next-line no-param-reassign
		li.Selected = !li.Selected;
		let report = this.getSelectClickedReport;
		if (li.Selected) {
			// report.mxrSelectClicked.carouselOffer = {};
			report.mxrSelectClicked!.shoppingListOffer = {
				id: li.Id,
				mappedBaseIngredientId: Number.parseInt(li.ItemId, 10),
				name: li.Name,
				price: li.Price,
				shoppingListItemName: li.Name,
				upc: li.Upc,
			};
		}
		if (!li.Selected) {
			report = this.getDeselectClickedReport;
			// report.mxrDeselectClicked.carouselOffer = {};
			report.mxrDeselectClicked!.shoppingListOffer = {
				id: li.Id,
				mappedBaseIngredientId: Number.parseInt(li.ItemId, 10),
				name: li.Name,
				price: li.Price,
				shoppingListItemName: li.Name,
				upc: li.Upc,
			};
		}
		this.sendReport(report);
		this.updateProductShoppingListItem(li);
		this.$forceUpdate();
	}

	private swap(item: ProductShoppingListItem, swapSection: number) {
		this.openMxrSwapModal({ item, swapSection });
		this.updateSwapOptions(item);
	}
}
